import { Package, DollarSign, TrendingUp, ShoppingBag, ExternalLink, Tag, Star, BarChart2 } from 'lucide-react'

const DEMAND_CONFIG = {
  very_high: { label: 'Muy Alta',  color: '#d0d0d0', bar: 100 },
  high:      { label: 'Alta',      color: '#a8a8a8', bar: 75  },
  medium:    { label: 'Media',     color: '#7a7a7a', bar: 50  },
  low:       { label: 'Baja',      color: '#4a4a4a', bar: 25  },
}

function ProductCard({ product }) {
  return (
    <div className="p-3 bg-slate-darker border border-slate-dark/20 rounded-lg hover:border-slate-dark/50 transition-all flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-slate-light font-medium">AliExpress</p>
        {product.url && product.url !== '#' && (
          <a href={product.url} target="_blank" rel="noopener noreferrer" className="text-slate-light hover:text-slate-lighter shrink-0">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      <p className="text-sm font-medium text-slate-lightest line-clamp-2 leading-snug">
        {product.title || 'Producto'}
      </p>

      <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-dark/30">
        <div>
          <span className="text-base font-bold text-slate-lighter">
            ${product.price_usd || '—'}
          </span>
          {product.original_price && product.original_price !== product.price_usd && (
            <span className="text-xs text-slate-medium line-through ml-1">${product.original_price}</span>
          )}
          {product.discount_pct > 0 && (
            <span className="text-xs text-slate-lighter ml-1.5 font-bold">-{product.discount_pct}%</span>
          )}
        </div>
        <div className="text-right">
          {product.rating && (
            <div className="flex items-center gap-1 justify-end">
              <Star className="w-3 h-3 text-slate-lighter fill-slate-lighter" />
              <span className="text-xs text-slate-lighter">{product.rating}</span>
            </div>
          )}
          {product.orders_count && (
            <p className="text-xs text-slate-light">{product.orders_count.toLocaleString()} pedidos</p>
          )}
        </div>
      </div>

      {product.store_name && (
        <p className="text-xs text-slate-light">Tienda: <span className="text-slate-lighter">{product.store_name}</span></p>
      )}
    </div>
  )
}

export default function DropshippingPanel({ dropshippingData }) {
  if (!dropshippingData) return null

  const { aliexpress, margin_analysis, demand_level } = dropshippingData

  const hasProducts = aliexpress?.products?.length > 0
  if (!hasProducts && !margin_analysis) return null

  const demandConf = DEMAND_CONFIG[demand_level] || DEMAND_CONFIG.medium

  return (
    <div className="gradient-glow p-6 rounded-xl card-hover space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ShoppingBag className="w-5 h-5 text-slate-lighter" />
        <div>
          <h3 className="text-lg font-bold text-white">Dropshipping — AliExpress</h3>
          <p className="text-xs text-slate-light mt-0.5">Análisis de proveedores y márgenes estimados</p>
        </div>
      </div>

      {/* Margin analysis highlight */}
      {margin_analysis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
            <DollarSign className="w-4 h-4 text-slate-light mx-auto mb-1" />
            <p className="text-lg font-bold text-slate-lighter">${margin_analysis.wholesale_price}</p>
            <p className="text-xs text-slate-light">Costo proveedor</p>
          </div>
          <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
            <Tag className="w-4 h-4 text-slate-light mx-auto mb-1" />
            <p className="text-lg font-bold text-slate-lighter">${margin_analysis.recommended_sale_price}</p>
            <p className="text-xs text-slate-light">Precio de venta</p>
          </div>
          <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
            <TrendingUp className="w-4 h-4 text-slate-light mx-auto mb-1" />
            <p className="text-lg font-bold text-slate-lighter">{margin_analysis.gross_margin_pct}%</p>
            <p className="text-xs text-slate-light">Margen bruto</p>
          </div>
          <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
            <BarChart2 className="w-4 h-4 text-slate-light mx-auto mb-1" />
            <p className="text-base font-bold" style={{ color: demandConf.color }}>{demandConf.label}</p>
            <p className="text-xs text-slate-light">Demanda</p>
          </div>
        </div>
      )}

      {/* Margin viability note */}
      {margin_analysis?.viability_note && (
        <div className="p-3 bg-slate-darker border border-slate-dark/20 rounded-lg">
          <p className="text-xs text-slate-300">
            <span className="font-semibold text-slate-lighter">Análisis de margen: </span>
            {margin_analysis.viability_note}
            {margin_analysis.is_viable !== undefined && (
              <span className={`ml-2 font-bold ${margin_analysis.is_viable ? 'text-slate-lighter' : 'text-red-400'}`}>
                {margin_analysis.is_viable ? '✓ Viable para dropshipping' : '✗ Margen insuficiente'}
              </span>
            )}
          </p>
        </div>
      )}

      {/* AliExpress products */}
      {hasProducts && (
        <div>
          <p className="text-xs text-slate-light font-medium mb-3 uppercase tracking-widest flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-slate-lighter rounded-full"></span>
            Productos encontrados en AliExpress
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {aliexpress.products.slice(0, 6).map((p, i) => (
              <ProductCard key={i} product={p} />
            ))}
          </div>
          {aliexpress.demand && (
            <p className="text-xs text-slate-light mt-3">
              Total estimado de órdenes:{' '}
              <span className="font-bold text-slate-lighter">
                {aliexpress.demand.total_orders?.toLocaleString() || 'N/D'}
              </span>
            </p>
          )}
        </div>
      )}

      {/* Data source note */}
      <p className="text-xs text-slate-medium border-t border-slate-dark/20 pt-3">
        Fuente: AliExpress · Los precios son orientativos y pueden variar según proveedor y volumen
      </p>
    </div>
  )
}
